import React, { useState } from "react";
import {
  FaChevronDown,
  FaFilter,
  FaRedoAlt,
  FaShieldAlt,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

function ToggleSwitch({ checked, onClick, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onClick}
      disabled={disabled}
      className={[
        "relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition",
        "focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900",
        checked
          ? "bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500"
          : "bg-slate-300 dark:bg-slate-700",
        disabled ? "cursor-not-allowed opacity-60" : "",
      ].join(" ")}
    >
      <span
        className={[
          "absolute left-1 top-1 h-6 w-6 rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-6" : "translate-x-0",
        ].join(" ")}
      />

      <span className="sr-only">Basculer la Self-Défense</span>
    </button>
  );
}

function GradeButton({ grade, active, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "min-h-11 rounded-2xl border px-3 text-sm font-bold transition",
        "focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 dark:focus:ring-offset-slate-950",
        active
          ? "border-violet-400 bg-violet-600 text-white shadow-md shadow-violet-200 dark:border-violet-400 dark:bg-violet-500 dark:shadow-black/20"
          : "border-slate-200 bg-white text-slate-700 hover:border-violet-200 hover:bg-violet-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:border-violet-700 dark:hover:bg-violet-950/30",
        disabled ? "cursor-not-allowed opacity-60" : "",
      ].join(" ")}
    >
      {grade}
    </button>
  );
}

function ControlsFields({
  grades,
  selectedGrades = [],
  onToggleGrade,
  onClearGrades,
  includeSelfDefense,
  onToggleSelfDefense,
  disabled,
}) {
  const allGradesSelected = selectedGrades.length === 0;

  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.8fr)] lg:items-start">
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Ceintures
          </span>

          {!allGradesSelected && (
            <button
              type="button"
              onClick={onClearGrades}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300"
            >
              <FaTimes className="text-[0.65rem]" />
              Tout afficher
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
          <button
            type="button"
            onClick={onClearGrades}
            disabled={disabled}
            className={[
              "min-h-11 rounded-2xl border px-3 text-sm font-bold transition",
              allGradesSelected
                ? "border-sky-400 bg-sky-600 text-white shadow-md shadow-sky-200 dark:border-sky-400 dark:bg-sky-500 dark:shadow-black/20"
                : "border-slate-200 bg-white text-slate-700 hover:border-sky-200 hover:bg-sky-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:border-sky-700 dark:hover:bg-sky-950/30",
              disabled ? "cursor-not-allowed opacity-60" : "",
            ].join(" ")}
          >
            Toutes
          </button>

          {grades.map((grade) => (
            <GradeButton
              key={grade}
              grade={grade}
              active={selectedGrades.includes(grade)}
              onClick={() => onToggleGrade?.(grade)}
              disabled={disabled}
            />
          ))}
        </div>

        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
          Tu peux sélectionner plusieurs ceintures pour mélanger les niveaux dans
          le même quizz.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950/70">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-violet-600 shadow-sm dark:bg-slate-900 dark:text-violet-300">
              <FaShieldAlt />
            </div>

            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Self-Défense
              </p>

              <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                {includeSelfDefense
                  ? "Les techniques de Self-Défense sont incluses."
                  : "Quizz limité aux techniques judo classiques."}
              </p>
            </div>
          </div>

          <ToggleSwitch
            checked={includeSelfDefense}
            onClick={onToggleSelfDefense}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

export default function QuizControls({
  grades = [],
  selectedGrades = [],
  onToggleGrade,
  onClearGrades,
  includeSelfDefense,
  onToggleSelfDefense,
  disabled = false,
  finished = false,
  onRestart,
  onRefresh,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">
            Paramètres du quizz
          </p>

          <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Sélectionne une ou plusieurs ceintures pour varier les questions.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900 lg:hidden"
          >
            <FaFilter />
            Filtres
            <FaChevronDown
              className={`text-xs transition-transform ${
                filtersOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={disabled}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
          >
            <FaSyncAlt className={disabled ? "animate-spin" : ""} />
            Actualiser
          </button>

          <button
            type="button"
            onClick={onRestart}
            disabled={disabled}
            className="col-span-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white shadow-lg shadow-slate-300/50 transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:shadow-black/20 sm:col-span-1"
          >
            <FaRedoAlt />
            {finished ? "Nouveau quizz" : "Recommencer"}
          </button>
        </div>
      </div>

      <div className="hidden lg:block">
        <ControlsFields
          grades={grades}
          selectedGrades={selectedGrades}
          onToggleGrade={onToggleGrade}
          onClearGrades={onClearGrades}
          includeSelfDefense={includeSelfDefense}
          onToggleSelfDefense={onToggleSelfDefense}
          disabled={disabled}
        />
      </div>

      {filtersOpen && (
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-lg shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-950 dark:shadow-black/20 lg:hidden">
          <ControlsFields
            grades={grades}
            selectedGrades={selectedGrades}
            onToggleGrade={onToggleGrade}
            onClearGrades={onClearGrades}
            includeSelfDefense={includeSelfDefense}
            onToggleSelfDefense={onToggleSelfDefense}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}